/**
 * ÂMEDORÉE — Email signup drawer
 *
 * Three steps: choose Menswear or Womenswear to reveal a mystery discount, enter an
 * email to claim it, then see the code (ready to copy, and applied at checkout).
 *
 * Opens after a delay from the Nth page view (or when the cursor leaves the page
 * on desktop), never again after signing up, and not for N days after closing.
 * The teaser bar at the bottom opens it. It shows from the first page view
 * (data-teaser="always") or only once the pop-up has appeared on that device, and
 * then on every page until they sign up. No thanks removes both for good on that device;
 * swiping down, tapping outside or the X (if shown) only closes the pop-up. Signs people up through Klaviyo's client
 * subscription API so the list's welcome flow runs as before.
 */
(function () {
  'use strict';

  if (customElements.get('amd-signup')) return;

  var KEYS = {
    done: 'amd_signup_done',
    dismissed: 'amd_signup_dismissed_at',
    pages: 'amd_signup_pages',
    pref: 'amd_signup_pref',
    teaserHidden: 'amd_signup_teaser_hidden',
    seen: 'amd_signup_seen',
    declined: 'amd_signup_declined'
  };

  function storage(kind) {
    try {
      var s = window[kind];
      var probe = '__amd';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) {
      return null;
    }
  }

  var local = storage('localStorage');
  var session = storage('sessionStorage');

  function get(store, key) {
    return store ? store.getItem(key) : null;
  }

  function set(store, key, value) {
    if (store) store.setItem(key, value);
  }

  function track(data) {
    try {
      (window.dataLayer = window.dataLayer || []).push(data);
    } catch (e) {}
  }

  class AmdSignup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.panel = this.querySelector('[data-amd-signup-panel]');
      this.form = this.querySelector('form');
      this.teaser = this.querySelector('[data-amd-signup-teaser]');
      this.designMode = this.hasAttribute('data-design-mode');
      this.pref = get(session, KEYS.pref) || '';

      this.querySelectorAll('[data-amd-signup-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-amd-signup-open]').forEach((button) => button.addEventListener('click', () => this.open()));
      this.querySelectorAll('[data-amd-signup-decline]').forEach((button) => button.addEventListener('click', () => this.decline()));
      this.querySelectorAll('[data-amd-teaser-close]').forEach((button) =>
        button.addEventListener('click', () => {
          set(session, KEYS.teaserHidden, '1');
          this.showTeaser(false);
        })
      );
      this.querySelectorAll('[data-amd-pref]').forEach((button) => button.addEventListener('click', () => this.choose(button.dataset.amdPref)));
      this.querySelectorAll('[data-amd-signup-copy]').forEach((button) => button.addEventListener('click', () => this.copy(button)));

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.setupSwipe();

      // Someone who already chose in this visit goes straight to the email step
      this.show(this.pref && this.dataset.start === 'choose' ? 'email' : this.dataset.start || 'choose', false);

      if (this.designMode) {
        if (this.dataset.teaser === 'always') this.showTeaser(true);
        var preview = () => {
          if (this.dataset.previewStep === 'success') this.showSuccess(this.pref || 'mens', false);
          else this.show(this.dataset.previewStep === 'email' ? 'email' : this.dataset.start || 'choose', false);
          this.open();
        };
        var inside = (event) => event.target && event.target.contains && event.target.contains(this);
        document.addEventListener('shopify:section:select', (event) => inside(event) && preview());
        document.addEventListener('shopify:section:load', (event) => inside(event) && preview());
        document.addEventListener('shopify:section:deselect', (event) => {
          if (!inside(event)) return;
          this.close(true);
          this.showTeaser(true);
        });
        return;
      }

      // Signed up, or said No thanks: no pop-up and no teaser bar on this device
      if (get(local, KEYS.done) || get(local, KEYS.declined)) return;

      var pages = parseInt(get(session, KEYS.pages) || '0', 10) + 1;
      set(session, KEYS.pages, String(pages));

      // The teaser is there from the start, or once the pop-up has appeared on this device
      if (!get(session, KEYS.teaserHidden) && (this.dataset.teaser === 'always' || get(local, KEYS.seen))) this.showTeaser(true);

      var dismissedAt = parseInt(get(local, KEYS.dismissed) || '0', 10);
      var waitDays = parseInt(this.dataset.days || '14', 10);
      if (dismissedAt && Date.now() - dismissedAt < waitDays * 86400000) return;

      if (pages < parseInt(this.dataset.pages || '1', 10)) return;

      var delay = parseInt(this.dataset.delay || '25', 10) * 1000;
      this.timer = window.setTimeout(() => this.open(), delay);

      if (window.matchMedia('(pointer: fine)').matches) {
        var armedAt = Date.now() + 5000;
        this.onLeave = (event) => {
          if (event.clientY <= 0 && !event.relatedTarget && Date.now() > armedAt) this.open();
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
    }

    open() {
      window.clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      if (this.dialog.open) return;
      if (document.querySelector('dialog[open]:not(.amd-signup__dialog)')) return;

      this.showTeaser(false);

      // Opening focuses the dialog itself, not a button in the panel. The panel is still
      // below the screen at that moment, and moving focus into it made iOS scroll the
      // pop-up, so the panel rose from the wrong place and jumped at the end. The panel is
      // inert while the dialog opens, so the browser has nothing in it to focus.
      var pageY = window.scrollY;
      this.panel.inert = true;
      this.dialog.showModal();
      this.panel.inert = false;
      this.dialog.scrollTop = 0;
      this.panel.scrollTop = 0;
      if (window.scrollY !== pageY) window.scrollTo(0, pageY);
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));

      // Once it has risen, focus moves to the step: the email field with a mouse, the heading on touch
      window.clearTimeout(this.focusTimer);
      this.focusTimer = window.setTimeout(() => {
        if (this.dialog.open) this.focusStep(this.querySelector('[data-step]:not([hidden])'));
      }, 450);

      if (this.designMode) return;
      set(local, KEYS.seen, '1');
      track({ event: 'newsletter_open', signup_source: 'signup_drawer' });
    }

    close(silent) {
      if (!this.dialog.open) return;

      this.dialog.classList.remove('is-open');
      this.panel.style.transform = '';
      document.documentElement.style.overflow = '';
      window.setTimeout(() => {
        if (this.dialog.open) this.dialog.close();
      }, 420);

      if (silent || this.designMode) return;

      if (!get(local, KEYS.done)) {
        set(local, KEYS.dismissed, String(Date.now()));
        if (!get(session, KEYS.teaserHidden)) this.showTeaser(true);
      }
    }

    // No thanks: closes the pop-up and removes the teaser bar for good on this device
    decline() {
      this.close(true);
      this.showTeaser(false);
      if (this.designMode) return;
      set(local, KEYS.declined, '1');
      track({ event: 'newsletter_declined', signup_source: 'signup_drawer', signup_step: this.dataset.step || '' });
    }

    showTeaser(show) {
      if (this.teaser) this.teaser.hidden = !show;
    }

    /* ---------- Steps ---------- */

    show(name, focus) {
      var target = this.querySelector('[data-step="' + name + '"]');
      if (!target) return;

      this.querySelectorAll('[data-step]').forEach((step) => {
        step.hidden = step !== target;
      });
      this.dataset.step = name;
      this.classList.toggle('is-revealed', name !== 'choose');

      if (focus !== false) this.focusStep(target);
    }

    focusStep(target) {
      if (!target) return;
      var input = target.querySelector('input[type="email"]');
      if (input && window.matchMedia('(pointer: fine)').matches) {
        input.focus({ preventScroll: true });
      } else {
        var heading = target.querySelector('.amd-signup__heading');
        if (heading) heading.focus({ preventScroll: true });
      }
    }

    choose(pref) {
      this.pref = pref;
      set(session, KEYS.pref, pref);
      this.classList.add('is-revealing');
      this.show('email');
      window.setTimeout(() => this.classList.remove('is-revealing'), 900);
      track({ event: 'newsletter_step', signup_step: 'choose', signup_pref: pref });
    }

    /* ---------- Email ---------- */

    async submit(event) {
      event.preventDefault();

      var input = this.form.querySelector('input[type="email"]');
      var error = this.form.querySelector('.amd-signup__error');
      var button = this.form.querySelector('button[type="submit"]');
      var email = (input.value || '').trim();
      var pref = this.pref;

      error.hidden = true;

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        error.textContent = 'Please enter a valid email address.';
        error.hidden = false;
        input.focus();
        return;
      }

      if (this.designMode) {
        this.showSuccess(pref || 'mens');
        return;
      }

      button.setAttribute('aria-busy', 'true');

      var company = this.dataset.company;
      var headers = { 'content-type': 'application/json', revision: '2024-10-15' };
      var list = this.dataset.list;
      var post = function (attributes) {
        return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: { custom_source: 'Signup drawer', profile: { data: { type: 'profile', attributes: attributes } } },
              relationships: { list: { data: { type: 'list', id: list } } }
            }
          })
        });
      };

      try {
        var response = await post({ email: email });
        if (!response.ok && response.status !== 202) throw new Error('Klaviyo ' + response.status);

        // Menswear or Womenswear on the profile, under the same property and values as the old Klaviyo
        // form (Preference = Menswear / Womenswear), so the existing segments pick it up. pref_category
        // is kept for the early access page. Saved in a second, separate request, as on that page.
        if (pref) {
          var properties = { pref_category: pref };
          var key = this.dataset.prefProperty || 'Preference';
          properties[key] = pref === 'womens' ? this.dataset.prefWomen || 'Womenswear' : this.dataset.prefMen || 'Menswear';
          post({ email: email, properties: properties }).catch(() => {});
        }

        set(local, KEYS.done, '1');
        this.applyCode();
        this.showSuccess(pref);

        try {
          if (window.fbq) window.fbq('track', 'Lead');
        } catch (e) {}
        track({ event: 'newsletter_signup', signup_source: 'signup_drawer', signup_pref: pref || '' });
      } catch (e) {
        error.textContent = 'Something went wrong. Please try again.';
        error.hidden = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    // Puts the code on their checkout right away, so it works even if they close the drawer
    applyCode() {
      var code = this.dataset.code;
      if (!code || this.dataset.autoApply !== 'true') return;
      fetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js', { credentials: 'same-origin' }).catch(() => {});
    }

    showSuccess(pref, focus) {
      this.show('success', focus);
      this.showTeaser(false);

      var link = this.querySelector('[data-amd-signup-shop]');
      if (!link) return;

      var women = pref === 'womens';
      var label = women ? this.dataset.shopWomenLabel : this.dataset.shopMenLabel;
      if (label) link.textContent = label;
      if (this.dataset.code) link.href = women ? this.dataset.shopWomen : this.dataset.shopMen;
      else link.href = women ? '/collections/women' : '/collections/men';
    }

    /* ---------- Copy the code ---------- */

    async copy(button) {
      var input = button.parentElement.querySelector('input');
      var code = input ? input.value : this.dataset.code;
      var done = () => {
        if (!button.dataset.label) button.dataset.label = button.textContent;
        button.textContent = button.dataset.copied || 'Copied';
        window.clearTimeout(this.copyTimer);
        this.copyTimer = window.setTimeout(() => {
          button.textContent = button.dataset.label;
        }, 2000);
      };

      try {
        await navigator.clipboard.writeText(code);
        done();
      } catch (e) {
        if (input) {
          input.select();
          input.setSelectionRange(0, code.length);
          try {
            document.execCommand('copy');
            done();
          } catch (err) {}
        }
      }
      track({ event: 'newsletter_code_copied', signup_source: 'signup_drawer' });
    }

    /* ---------- Swipe down to close (mobile) ---------- */

    setupSwipe() {
      var startY = null;
      var delta = 0;
      var mobile = window.matchMedia('(max-width: 699px)');

      this.panel.addEventListener(
        'touchstart',
        (event) => {
          if (!mobile.matches || event.target.closest('input, button, a, label')) return;
          startY = event.touches[0].clientY;
          delta = 0;
          this.panel.classList.add('is-dragging');
        },
        { passive: true }
      );

      this.panel.addEventListener(
        'touchmove',
        (event) => {
          if (startY === null) return;
          delta = Math.max(0, event.touches[0].clientY - startY);
          this.panel.style.transform = 'translateY(' + delta + 'px)';
        },
        { passive: true }
      );

      this.panel.addEventListener('touchend', () => {
        if (startY === null) return;
        this.panel.classList.remove('is-dragging');
        startY = null;
        if (delta > 90) {
          this.close();
        } else {
          this.panel.style.transform = '';
        }
      });
    }
  }

  customElements.define('amd-signup', AmdSignup);
})();
